import React from 'react';
import StatCard from 'components/v2/StatCard';
import { getClassName } from 'utils/ui/classes';
import { useTranslation } from 'react-i18next';
import formatMoney from 'utils/formatters/Money';

export default function Summary({
  shipperTotal,
  carrierTotal,
  vendorTotal,
  grossProfit,
  grossMargin,
  shipperFSC,
  carrierFSC,
  tariff,
  intermodalShipmentType,
  currency,
  pendingCogs,
  pendingRevenue,
}) {
  const { t } = useTranslation();
  let columns = 4;
  if (intermodalShipmentType && vendorTotal) {
    columns += 1;
  }

  if (!intermodalShipmentType && tariff) {
    columns += 1;
  }

  if (!intermodalShipmentType && shipperFSC) {
    columns += 1;
  }

  if (!intermodalShipmentType && carrierFSC) {
    columns += 1;
  }
  if (intermodalShipmentType) {
    columns += 2;
  }

  const showTariff = !intermodalShipmentType && tariff;
  const showFSC = !intermodalShipmentType && (shipperFSC || carrierFSC);
  const showVendorTotal = Boolean(intermodalShipmentType && vendorTotal);
  const showPendingRevenue = intermodalShipmentType;
  const showPendingCogs = intermodalShipmentType;

  const className = getClassName(
    'grid grid-cols-1 rounded-lg bg-white overflow-hidden shadow divide-y divide-gray-200 md:divide-y-0 md:divide-x mb-4',
    {
      'md:grid-cols-2': columns === 2,
      'md:grid-cols-3': columns === 3,
      'md:grid-cols-4': columns === 4,
      'md:grid-cols-5': columns === 5,
      'md:grid-cols-6': columns === 6,
      'md:grid-cols-7': columns === 7,
      'md:grid-cols-8': columns === 8,
      'md:grid-cols-9': columns === 9,
      'md:grid-cols-10': columns === 10,
      'sm:max-w-lg': columns < 4,
    },
  );

  return (
    <dl data-testid="dl-b8603718" className={className}>
      <StatCard
        dataTestId="StatCard-ea1d9613"
        title={t('REVENUE')}
        key="shipper_total"
      >
        <div
          data-testid="div-605baf66"
          className="flex items-baseline text-lg font-medium text-gray-800"
        >
          {formatMoney(shipperTotal, currency)}
        </div>
      </StatCard>
      <StatCard
        dataTestId="StatCard-16a4aed5"
        title={t('CARRIER_COSTS')}
        key="carrier_total"
      >
        <div
          data-testid="div-f9c432b3"
          className="flex items-baseline text-lg font-medium text-gray-800"
        >
          {formatMoney(carrierTotal, currency)}
        </div>
      </StatCard>
      {showVendorTotal && (
        <StatCard
          dataTestId="StatCard-cb69c733"
          title={t('VENDOR_COSTS')}
          key="vendor_total"
        >
          <div
            data-testid="div-fdf41b17"
            className="flex items-baseline text-lg font-medium text-gray-800"
          >
            {formatMoney(vendorTotal, currency)}
          </div>
        </StatCard>
      )}
      <StatCard
        dataTestId="StatCard-4e2bef13"
        title={t('GROSS_PROFIT')}
        key="gross_profit"
      >
        <div
          data-testid="div-fdf41b18"
          className={`flex items-baseline text-lg font-medium ${
            grossProfit >= 0 ? 'text-gray-800' : 'text-red-800'
          }`}
        >
          {formatMoney(grossProfit, currency)}
        </div>
      </StatCard>
      <StatCard
        dataTestId="StatCard-4e2bef10"
        title={t('GROSS_MARGIN')}
        key="gross_margin"
      >
        <div
          data-testid="div-fdf41b19"
          className={`flex items-baseline text-lg font-medium ${
            grossMargin >= 0 ? 'text-gray-800' : 'text-red-800'
          }`}
        >
          {shipperTotal <= 0
            ? '-'
            : `${grossMargin === '-0' ? 0 : grossMargin}%`}
        </div>
      </StatCard>
      {showPendingRevenue && (
        <StatCard
          dataTestId="StatCard-4e2bef11"
          title={t('UNACTIONED_PENDING_REVENUE')}
          key="unactioned_pending_revenue"
        >
          <div
            data-testid="div-fdf41b20"
            className={`flex items-baseline text-lg font-medium ${
              pendingRevenue >= 0 ? 'text-gray-800' : 'text-red-800'
            }`}
          >
            {formatMoney(pendingRevenue, currency)}
          </div>
        </StatCard>
      )}
      {showPendingCogs && (
        <StatCard
          dataTestId="StatCard-4e2bef12"
          title={t('UNACTIONED_PENDING_COGS')}
          key="unactioned_pending_cogs"
        >
          <div
            data-testid="div-fdf41b21"
            className={`flex items-baseline text-lg font-medium ${
              pendingCogs >= 0 ? 'text-gray-800' : 'text-red-800'
            }`}
          >
            {formatMoney(pendingCogs, currency)}
          </div>
        </StatCard>
      )}
      {showFSC && shipperFSC && (
        <StatCard
          dataTestId="StatCard-4e2bef09"
          title={t('SHIPPER_FSC')}
          key="shipperFSC"
        >
          <div
            data-testid="div-77cb502d"
            className="flex items-baseline text-lg font-medium text-gray-800"
          >
            {shipperFSC}%
          </div>
        </StatCard>
      )}
      {showFSC && carrierFSC && (
        <StatCard
          dataTestId="StatCard-44191632"
          title={t('CARRIER_FSC')}
          key="carrier-shipperFSC"
        >
          <div
            data-testid="div-c617cecd"
            className="flex items-baseline text-lg font-medium text-gray-800"
          >
            {carrierFSC}%
          </div>
        </StatCard>
      )}
      {showTariff && (
        <StatCard
          dataTestId="StatCard-28433e4a"
          title={t('TARIFF')}
          className=""
        >
          <div
            data-testid="div-6a854141"
            className="flex items-baseline font-medium text-gray-800 text-base"
          >
            {tariff}
          </div>
        </StatCard>
      )}
    </dl>
  );
}
